import type {
  QuestionCardViewModel,
  StudentQuestionDetail,
  StudentQuestionListItem,
} from "../types"

/** Strip HTML tags for card snippet preview. */
export const stripHtml = (html: string | null | undefined): string => {
  if (!html) return ""
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
}

export const buildTopicPath = (item: StudentQuestionListItem): string => {
  const parts = [
    item.topic,
    item.level_2_name,
    item.level_3_name,
    item.level_4_name,
  ].filter(Boolean)
  return parts.join(" › ")
}

export const firstSubPartLabel = (
  subParts: Array<{ label?: string | null }> | null | undefined
): string | null => {
  const label = subParts?.[0]?.label
  return label && String(label).trim() ? String(label).trim() : null
}

export const mapDetailToListItem = (
  detail: StudentQuestionDetail
): StudentQuestionListItem => {
  const hierarchy = detail.topic_hierarchy
  return {
    id: detail.id,
    subject: detail.subject,
    subject_title: detail.subject_title,
    chapter: hierarchy?.chapter ?? null,
    topic: hierarchy?.topic ?? null,
    chapter_id: hierarchy?.chapter_id ?? null,
    topic_id: hierarchy?.topic_id ?? null,
    level_2_id: hierarchy?.level_2_id ?? null,
    level_2_name: hierarchy?.level_2_name ?? null,
    level_3_id: hierarchy?.level_3_id ?? null,
    level_3_name: hierarchy?.level_3_name ?? null,
    level_4_id: hierarchy?.level_4_id ?? null,
    level_4_name: hierarchy?.level_4_name ?? null,
    grade: detail.grade,
    complexity: detail.complexity,
    source: detail.source,
    paper: detail.paper,
    year: detail.year,
    marks: detail.marks,
    question: detail.resources?.question?.content ?? null,
    solution: detail.resources?.solution?.content ?? null,
    marking_scheme: detail.resources?.marking_scheme?.content ?? null,
    sub_parts: detail.sub_parts,
    question_artifacts: detail.resources?.question?.artifacts,
    solution_artifacts: detail.resources?.solution?.artifacts,
    isBookmarked: !!detail.student?.is_bookmarked,
    created_at: null,
    updated_at: null,
  }
}

export const pinQuestionToTop = (
  items: StudentQuestionListItem[],
  questionId: string | null | undefined,
  fallback?: StudentQuestionListItem | null
): StudentQuestionListItem[] => {
  if (!questionId) return items
  const rest = items.filter((item) => item.id !== questionId)
  const existing = items.find((item) => item.id === questionId)
  if (existing) return [existing, ...rest]
  if (fallback?.id === questionId) return [fallback, ...rest]
  return items
}

export const mapQuestionCard = (
  item: StudentQuestionListItem
): QuestionCardViewModel => ({
  id: item.id,
  subjectTitle: item.subject_title || "Subject",
  yearLabel: item.year != null ? String(item.year) : "—",
  typeLabel: item.paper || item.source || "Paper",
  difficulty: item.complexity || "—",
  snippet: stripHtml(item.question) || "No preview available",
  questionHtml: item.question || "",
  topicPath: buildTopicPath(item),
  isBookmarked: !!item.isBookmarked,
  hasSolution: !!stripHtml(item.solution),
  hasMarkingScheme: !!stripHtml(item.marking_scheme),
  marks: item.marks,
  partLabel: firstSubPartLabel(item.sub_parts),
  questionArtifacts: item.question_artifacts,
})
